// File: src/components/forms/SmartSelect/PickerModal.tsx

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Typography, Button } from "../../../components";

interface PickerModalProps {
  open: boolean;
  value: string;
  options: { label: string; value: string }[];
  onSelect: (value: string) => void;
  onClose: () => void;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlay};
  z-index: 999;
`;

const Sheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border-top-left-radius: ${({ theme }) => theme.radii.lg};
  border-top-right-radius: ${({ theme }) => theme.radii.lg};
  padding-bottom: env(safe-area-inset-bottom);
  max-height: 55vh;
  overflow: hidden;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const OptionList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.sm} 0;
`;

const OptionItem = styled.div<{ selected: boolean }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  text-align: center;
  font-weight: ${({ selected }) => (selected ? 600 : 500)};
  font-size: 1.05rem;
  border-radius: ${({ theme }) => theme.radii.md};
  margin: ${({ theme }) => theme.spacing.xs} auto;
  max-width: 90%;
  background: ${({ selected, theme }) =>
    selected ? theme.colors.primary  : "transparent"};
  color: ${({ selected, theme }) =>
    selected ? theme.colors.textonPrimary : theme.colors.textPrimary};
  transition: background 0.2s ease;
  cursor: pointer;

  &:active {
    background: ${({ theme }) => theme.colors.primarySelected + "33"};
  }
`;

export const PickerModal: React.FC<PickerModalProps> = ({
  open,
  value,
  options,
  onSelect,
  onClose,
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (open) setLocalValue(value);
  }, [value, open]);

  const handleDone = () => {
    onSelect(localValue);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <Backdrop onClick={onClose} />
      <Sheet>
        <Header>
          <Button size="compact" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Typography variant="body" weight="medium" as="span">
            Select Option
          </Typography>
          <Button size="compact" onClick={handleDone}>
            Done
          </Button>
        </Header>
        <OptionList>
          {options.map((opt) => (
            <OptionItem
              key={opt.value}
              selected={opt.value === localValue}
              onClick={() => setLocalValue(opt.value)}
            >
              {opt.label}
            </OptionItem>
          ))}
        </OptionList>
      </Sheet>
    </>
  );
};